// src/components/common/ProfileSwitcher.jsx
import React, { useState, useRef, useEffect } from 'react';
import { 
  ChevronDown, 
  School, 
  Building, 
  Briefcase, 
  Check, 
  Plus,
  User,
  MapPin
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const ProfileSwitcher = () => {
  const { userContexts, activeContext, switchContext } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getContextIcon = (context) => {
    switch (context.type) {
      case 'institution':
        return context.data.type === 'School' 
          ? <School className="w-4 h-4" />
          : <Building className="w-4 h-4" />;
      case 'project':
        return <Briefcase className="w-4 h-4" />;
      default:
        return <Building className="w-4 h-4" />;
    }
  };

  const getContextBadge = (context) => {
    const colors = {
      'School': 'bg-blue-100 text-blue-800',
      'District': 'bg-green-100 text-green-800',
      'Province': 'bg-purple-100 text-purple-800',
      'Project': 'bg-orange-100 text-orange-800'
    };

    const badgeColor = colors[context.institutionType] || 'bg-gray-100 text-gray-800';
    
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${badgeColor}`}>
        {context.institutionType}
      </span>
    );
  };

  const handleContextSwitch = async (contextId) => {
    await switchContext(contextId);
    setIsOpen(false);
  };

  if (!activeContext || userContexts.length === 0) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Current Context Display */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition-colors min-w-0"
      >
        <div className="flex items-center gap-2 min-w-0">
          {getContextIcon(activeContext)}
          <div className="text-left min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
              {activeContext.name}
            </div>
            <div className="text-xs text-gray-500 truncate max-w-[200px]">
              {activeContext.subtitle}
            </div>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-y-auto">
          <div className="p-3 border-b border-gray-100">
            <h4 className="text-sm font-medium text-gray-900">Switch Context</h4>
            <p className="text-xs text-gray-500">Choose your active workspace</p>
          </div>
          
          <div className="py-2">
            {userContexts.map((context) => (
              <button
                key={context.id}
                onClick={() => handleContextSwitch(context.id)}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors"
              >
                <div className="flex-shrink-0">
                  {getContextIcon(context)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {context.name}
                    </div>
                    {getContextBadge(context)}
                    {context.status === 'archived' && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                        Archived
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <MapPin className="w-3 h-3" />
                    <span className="truncate">{context.location}</span>
                  </div>
                  
                  <div className="text-xs text-gray-400 mt-1">
                    Role: {context.role}
                  </div>
                </div>
                
                {activeContext.id === context.id && (
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
          
          {/* Add New Context */}
          <div className="border-t border-gray-100 p-2">
            <Link
              to="/settings"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
            >
              <Plus className="w-4 h-4" />
              Manage Institutions & Projects
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSwitcher;